import React, { useCallback, useEffect, useRef, useState } from "react";
import "./SitePet.css";
import { getPetLinePack, resolvePetDialogue } from "./SitePetDialogue";
import {
  getStoredPetCard,
  subscribeToPetCardChange,
  recordPetEvent,
  recordPetVisitOnce,
  recordActivePetTime,
  syncPetToSupabase,
} from "./SitePetProgress";

const FALLBACK_CHARACTER_ID = "trex";

const spriteContext = require.context(
  "./assets/site-pet",
  true,
  /\.(png|webp|gif)$/
);

const spriteKeys = spriteContext.keys();

const SPRITE_NAMES = {
  idle: ["walk1"],
  walk: ["walk1", "walk2", "walk3", "walk4"],
  drag: ["drag1", "drag2", "drag3"],
  jump: ["jump1", "jump2", "jump3", "jump4", "jump5"],
  fall: ["fall1", "fall2", "fall3", "fall4", "fall5"],
  happy: ["happy1", "happy2", "happy3"],
  textmsg: ["msg1", "msg2", "msg3", "msg4"],
  sleep: ["sleep1", "sleep2", "sleep3", "sleep4"],
  wake: ["wake1", "wake2", "wake3", "wake4", "wake5"],
};

const FRAME_MS = {
  idle: 520,
  walk: 180,
  drag: 150,
  jump: 115,
  fall: 125,
  happy: 140,
  textmsg: 120,
  sleep: 180,
  wake: 120,
};

const FALLBACK_LINES = {
  random: [
    "I am keeping an eye on the page.",
    "This portfolio has good walking routes.",
    "Tiny patrol, big responsibility.",
  ],
  hover: [
    "Hi! That tickles a little.",
    "Hello there. I will pause for you.",
    "I will stand still for inspection.",
  ],
  click: ["Boop received.", "I can hop for that.", "Tiny bounce activated."],
  footer: [
    "Footer reached. I will wave politely.",
    "This is a nice final ledge.",
    "I can guard the footer from here.",
  ],
  scroll: [
    "Whoa, moving walkway.",
    "The page is sliding again.",
    "I will keep my balance.",
  ],
  edge: [
    "Little steps near the edge.",
    "I will turn around here.",
    "End of the div. Turning around.",
  ],
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const pick = (items) => {
  const safeItems = Array.isArray(items) && items.length ? items : [""];
  return safeItems[Math.floor(Math.random() * safeItems.length)];
};

const findSpritePath = (folder, name) => {
  const candidates = [
    `./${folder}/${name}.png`,
    `./${folder}/${name}.webp`,
    `./${folder}/${name}.gif`,
  ];

  return candidates.find((path) => spriteKeys.includes(path));
};

const loadCharacterSprites = (characterId = FALLBACK_CHARACTER_ID) => {
  const folder = characterId || FALLBACK_CHARACTER_ID;

  const loadSprite = (name) => {
    const path =
      findSpritePath(folder, name) ||
      findSpritePath(FALLBACK_CHARACTER_ID, name);

    if (!path) {
      throw new Error(
        `Missing sprite "${name}" for "${folder}" and fallback "${FALLBACK_CHARACTER_ID}".`
      );
    }

    return spriteContext(path);
  };

  return Object.fromEntries(
    Object.entries(SPRITE_NAMES).map(([state, names]) => [
      state,
      names.map(loadSprite),
    ])
  );
};

function SitePet() {
  const petRef = useRef(null);
  const spriteRef = useRef(null);
  const speechTimerRef = useRef(null);
  const [speech, setSpeech] = useState("");
  const [stateName, setStateName] = useState("fall");

  const activeCardRef = useRef(getStoredPetCard());
  const linePackRef = useRef(
    getPetLinePack(activeCardRef.current?.characterId || "default")
  );
  const spritePackRef = useRef(
    loadCharacterSprites(activeCardRef.current?.characterId || FALLBACK_CHARACTER_ID)
  );

  const getLine = useCallback((type) => {
    return pick(linePackRef.current?.[type] || FALLBACK_LINES[type]);
  }, []);

  const physicsRef = useRef({
    x: 40,
    y: 80,
    vx: 0.55,
    vy: 0,
    direction: 1,
    onGround: false,
    dragging: false,
    dragOffsetX: 0,
    dragOffsetY: 0,
    pointerId: null,
    pointerLastX: 0,
    pointerLastY: 0,
    pointerLastTime: 0,
    pointerVelocityX: 0,
    pointerVelocityY: 0,
    dragDistance: 0,
    wasDragged: false,
    state: "fall",
    frame: 0,
    frameElapsed: 0,
    pauseUntil: 0,
    platforms: [],
    lastPlatformElement: null,
    lastPlatformMessageAt: 0,
    lastEdgeMessageAt: 0,
    lastTopMessageAt: 0,
    lastScrollY: 0,
    lastScrollMessageAt: 0,
    lastPlatformRefreshAt: 0,
    footerLimitTop: null,
    nextRandomJumpAt: 0,
    previousTime: 0,
    raf: null,
    chatterTimer: null,
    dialogueMemory: {},
    lastTextScanAt: 0,
    lastContextSpeechAt: 0,
    visibleTextItems: [],
    interactionCount: 0,
    lastInteractionAt: 0,
    happyUntil: 0,
    clickCount: 0,
    textMsgUntil: 0,
    lastUserActionAt: performance.now(),
    isSleeping: false,
    isWaking: false,
    sleepLocked: false,
    sleepSoundIndex: 0,
    lastSleepSoundAt: 0,
  });

  useEffect(() => {
    recordPetVisitOnce();

    const unsubscribe = subscribeToPetCardChange((card) => {
      activeCardRef.current = card;
      linePackRef.current = getPetLinePack(card?.characterId || "default");
      spritePackRef.current = loadCharacterSprites(
        card?.characterId || FALLBACK_CHARACTER_ID
      );

      if (spriteRef.current) {
        const pet = physicsRef.current;
        const frames =
          spritePackRef.current[pet.state] || spritePackRef.current.idle;

        spriteRef.current.src = frames[pet.frame] || frames[0];
      }
    });

 const timer = window.setInterval(() => {
      recordActivePetTime(5000);
      syncPetToSupabase();
    }, 5000);


    return () => {
      unsubscribe();
      window.clearInterval(timer);
    };
  }, []);

  const say = useCallback((line, duration = 2400) => {
    if (!line) return;

    window.clearTimeout(speechTimerRef.current);
    setSpeech(line);

    speechTimerRef.current = window.setTimeout(() => {
      setSpeech("");
    }, duration);
  }, []);

  useEffect(() => {
    const node = petRef.current;
    const sprite = spriteRef.current;

    if (!node || !sprite) return undefined;

    const pet = physicsRef.current;
    pet.lastScrollY = window.scrollY || document.documentElement.scrollTop || 0;

    const getWidth = () => node.offsetWidth || 72;
    const getHeight = () => node.offsetHeight || 100;

    const applyPosition = () => {
      node.style.transform = `translate3d(${pet.x}px, ${pet.y}px, 0)`;
      sprite.style.transform = `scaleX(${pet.direction})`;
    };

    const getDirectText = (element) =>
      Array.from(element.childNodes)
        .filter((nodeItem) => nodeItem.nodeType === Node.TEXT_NODE)
        .map((nodeItem) => nodeItem.textContent.trim())
        .filter(Boolean)
        .join(" ");

    const scanVisibleText = (time) => {
      if (time - pet.lastTextScanAt < 450) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const elements = Array.from(
        document.body.querySelectorAll(
          "h1,h2,h3,h4,h5,h6,p,span,a,button,li,strong,em,small,[data-pet-talk]"
        )
      );

      pet.visibleTextItems = elements
        .map((element) => {
          const rect = element.getBoundingClientRect();
          const talk = element.dataset.petTalk || "";
          const directText = getDirectText(element);
          const text = directText || (talk ? element.textContent.trim() : "");

          return {
            text,
            talk,
            rect,
            priority: Number(element.dataset.petTalkPriority || 0),
            cooldown: Number(element.dataset.petTalkCooldown || 7000),
            distance: Number(element.dataset.petTalkDistance || 0),
            elementKey: text.slice(0, 30),
          };
        })
        .filter(
          (item) =>
            (item.text || item.talk) &&
            item.rect.width > 0 &&
            item.rect.height > 0 &&
            item.rect.right >= 0 &&
            item.rect.left <= viewportWidth &&
            item.rect.bottom >= 0 &&
            item.rect.top <= viewportHeight
        );

      pet.lastTextScanAt = time;
    };

    const maybeSpeakFromVisibleText = (time) => {
      if (time - pet.lastContextSpeechAt < 900 || pet.dragging) return;

      scanVisibleText(time);

      const line = resolvePetDialogue({
        visibleTextItems: pet.visibleTextItems,
        petRect: node.getBoundingClientRect(),
        now: time,
        memory: pet.dialogueMemory,
        characterId: activeCardRef.current?.characterId || "default",
      });

      if (!line) return;

      say(line, 2600);
      pet.lastContextSpeechAt = time;
    };

    const maybeTriggerHappy = () => {
      const now = performance.now();

      if (now < pet.happyUntil) return;
      if (pet.interactionCount < 3) return;
      if (Math.floor(Math.random() * 2) !== 0) return;

      pet.happyUntil = now + 900;
      pet.interactionCount = 0;

      say("I like this energy.", 1800);
    };

    const maybeTriggerTextMsg = () => {
      const now = performance.now();

      if (now < pet.textMsgUntil) return;

      if (pet.clickCount >= 10) {
        pet.textMsgUntil = now + 1200;
        pet.clickCount = 0;
        say("Message received.", 1800);
        return;
      }

      if (Math.floor(Math.random() * 6) === 0) {
        pet.textMsgUntil = now + 1200;
        pet.clickCount = 0;
        say("Incoming signal.", 1800);
      }
    };

    const refreshPlatforms = () => {
      const petHeight = getHeight();
      const petWidth = getWidth();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const platformElements = Array.from(
        new Set([
          ...document.querySelectorAll(
            "div[data-platform], img, .footer-content, [data-pet-platform]"
          ),
        ])
      ).filter((element) => element !== node && !node.contains(element));

      pet.platforms = platformElements
        .map((element) => {
          const rect = element.getBoundingClientRect();
          const isFooter =
            element.classList.contains("frame6") ||
            element.dataset.petFooterPlatform !== undefined;

          const scale = clamp(
            Number(
              element.dataset.petPlatformScale ||
                element.dataset.platformScale ||
                1
            ),
            0.15,
            1.5
          );

          const inset = Math.max(
            Number(
              element.dataset.petPlatformInset ||
                element.dataset.platformInset ||
                0
            ),
            0
          );

          const scaledWidth = rect.width * scale;
          const sideInset = Math.max((rect.width - scaledWidth) / 2, 0) + inset;

          return {
            element,
            left: rect.left + sideInset,
            right: rect.right - sideInset,
            top: rect.top,
            bottom: rect.bottom,
            width: Math.max(rect.width - sideInset * 2, 0),
            isFooter,
            message: element.dataset.petMessage || (isFooter ? getLine("footer") : ""),
          };
        })
        .filter(
          (platform) =>
            platform.width > Math.max(24, petWidth * 0.7) &&
            platform.bottom > -petHeight &&
            platform.top < viewportHeight + petHeight
        );

      const visibleFooter = pet.platforms.find(
        (platform) =>
          platform.isFooter && platform.top > 0 && platform.top < viewportHeight
      );

      pet.footerLimitTop = visibleFooter ? visibleFooter.top : null;

      const floorTop = visibleFooter ? visibleFooter.top : viewportHeight - 10;

      pet.platforms.push({
        element: null,
        left: -160,
        right: viewportWidth + 160,
        top: floorTop,
        bottom: floorTop + 10,
        width: viewportWidth + 320,
        isFooter: false,
        message: visibleFooter
          ? getLine("footer")
          : "100vh floor. I can walk safely here.",
      });

      pet.platforms.sort((a, b) => a.top - b.top);
      pet.lastPlatformRefreshAt = performance.now();
    };

    const placePet = () => {
      const width = getWidth();
      const height = getHeight();

      pet.x = clamp(window.innerWidth * 0.18, 18, window.innerWidth - width - 18);
      pet.y = clamp(window.innerHeight * 0.3, 18, window.innerHeight - height - 18);
      pet.vx = 0.58;
      pet.vy = 1;

      node.style.opacity = "1";

      applyPosition();
      refreshPlatforms();
    };

    const announcePlatform = (platform) => {
      const now = performance.now();

      if (
        platform.element &&
        platform.element !== pet.lastPlatformElement &&
        platform.message &&
        now - pet.lastPlatformMessageAt > 1800
      ) {
        say(platform.message, 2600);
        pet.lastPlatformMessageAt = now;
      }

      pet.lastPlatformElement = platform.element;
    };

    const keepOnPlatform = (width) => {
      if (!pet.onGround) return;

      const platform = pet.platforms.find(
        (item) => item.element === pet.lastPlatformElement
      );

      if (!platform || !platform.element) return;

      const footLeft = pet.x + width * 0.28;
      const footRight = pet.x + width * 0.72;
      const now = performance.now();

      if (pet.direction > 0 && footRight > platform.right - 8) {
        pet.direction = -1;
        pet.vx = -Math.max(Math.abs(pet.vx), 0.55);

        if (now - pet.lastEdgeMessageAt > 2200) {
          say(getLine("edge"), 1900);
          pet.lastEdgeMessageAt = now;
        }
      }

      if (pet.direction < 0 && footLeft < platform.left + 8) {
        pet.direction = 1;
        pet.vx = Math.max(Math.abs(pet.vx), 0.55);

        if (now - pet.lastEdgeMessageAt > 2200) {
          say(getLine("edge"), 1900);
          pet.lastEdgeMessageAt = now;
        }
      }
    };

    const collideWithPlatforms = (previousY) => {
      const width = getWidth();
      const height = getHeight();
      const previousBottom = previousY + height;
      const nextBottom = pet.y + height;
      const footLeft = pet.x + width * 0.24;
      const footRight = pet.x + width * 0.76;
      const footCenter = pet.x + width * 0.5;
      const landingTolerance = Math.max(8, Math.abs(pet.vy) + 4);

      pet.onGround = false;

      const landingPlatform = pet.platforms
        .filter((platform) => {
          const feetOverlap = footRight >= platform.left && footLeft <= platform.right;
          const centerSupported =
            footCenter >= platform.left && footCenter <= platform.right;
          const crossesTop =
            previousBottom <= platform.top + landingTolerance &&
            nextBottom >= platform.top;

          return pet.vy >= 0 && crossesTop && (feetOverlap || centerSupported);
        })
        .sort((a, b) => a.top - b.top)[0];

      if (landingPlatform) {
        pet.y = landingPlatform.top - height;
        pet.vy = 0;
        pet.onGround = true;
        announcePlatform(landingPlatform);
      }

      keepOnPlatform(width);
    };

    const maybeRandomJump = (time) => {
      if (!pet.onGround || !pet.lastPlatformElement || time < pet.nextRandomJumpAt) {
        return;
      }

      pet.nextRandomJumpAt = time + 1300;

      if (Math.floor(Math.random() * 15) !== 0) return;

      pet.vy = -7.2;
      pet.vx = clamp(pet.direction * 2.1, -2.8, 2.8);
      pet.onGround = false;
      pet.lastPlatformElement = null;
      pet.nextRandomJumpAt = time + 2600;

      recordPetEvent("jump");
      say("Random hop. Let us see where I land.", 1800);
    };

    const setSpriteForState = (nextState, delta) => {
      const frames =
        spritePackRef.current[nextState] || spritePackRef.current.idle;

      if (pet.state !== nextState) {
        pet.state = nextState;
        pet.frame = 0;
        pet.frameElapsed = 0;
        sprite.src = frames[0];
        setStateName(nextState);
        return;
      }

      pet.frameElapsed += delta;

      if (pet.frameElapsed < FRAME_MS[nextState]) return;

      pet.frameElapsed %= FRAME_MS[nextState];

      if (nextState === "sleep") {
        if (!pet.sleepLocked) {
          if (pet.frame < frames.length - 1) {
            pet.frame += 1;
            sprite.src = frames[pet.frame];
          } else {
            pet.sleepLocked = true;
          }
        }
        return;
      }

      if (nextState === "wake") {
        if (pet.frame < frames.length - 1) {
          pet.frame += 1;
          sprite.src = frames[pet.frame];
        } else {
          pet.isWaking = false;
        }
        return;
      }

      if (nextState === "textmsg") {
        if (pet.frame < frames.length - 1) {
          pet.frame += 1;
          sprite.src = frames[pet.frame];
        }
        return;
      }

      pet.frame = (pet.frame + 1) % frames.length;
      sprite.src = frames[pet.frame];
    };

    const chooseState = () => {
      const now = performance.now();

      if (pet.isWaking) return "wake";
      if (pet.isSleeping) return "sleep";
      if (now < pet.textMsgUntil) return "textmsg";
      if (now < pet.happyUntil) return "happy";
      if (pet.dragging) return "drag";

      if (!pet.onGround) return pet.vy < -0.2 ? "jump" : "fall";

      return Math.abs(pet.vx) > 0.14 && now > pet.pauseUntil ? "walk" : "idle";
    };

    const step = (time) => {
      const previousTime = pet.previousTime || time;
      const delta = Math.min(time - previousTime, 34);
      const tick = delta / 16.67;
      pet.previousTime = time;

      const now = performance.now();

      if (
        !pet.isSleeping &&
        !pet.isWaking &&
        !pet.dragging &&
        Math.abs(pet.vy) < 0.5 &&
        now - pet.lastUserActionAt > 120000
      ) {
        pet.isSleeping = true;
        pet.sleepLocked = false;
        pet.frame = 0;
        pet.frameElapsed = 0;
        pet.vx = 0;
        pet.vy = 0;
        pet.sleepSoundIndex = 0;
        pet.lastSleepSoundAt = 0;
        say("brr...", 1400);
      }

      if (pet.isSleeping && now - pet.lastUserActionAt < 300) {
        pet.isSleeping = false;
        pet.isWaking = true;
        pet.frame = 0;
        pet.frameElapsed = 0;
        pet.sleepSoundIndex = 0;
        pet.lastSleepSoundAt = 0;
        say("Oh... you're back.", 2000);
      }

      if (pet.isSleeping) {
        const sleepSounds = ["brr...", "brrrr...", "brrrrr...", "brr..."];

        if (now - pet.lastSleepSoundAt > 1600) {
          say(sleepSounds[pet.sleepSoundIndex], 1400);
          pet.sleepSoundIndex = (pet.sleepSoundIndex + 1) % sleepSounds.length;
          pet.lastSleepSoundAt = now;
        }
      }

      if (time - pet.lastPlatformRefreshAt > 250) {
        refreshPlatforms();
      }

      const width = getWidth();
      const height = getHeight();

      if (!pet.dragging && !pet.isSleeping) {
        const previousY = pet.y;

        if (performance.now() > pet.pauseUntil && Math.abs(pet.vy) < 0.5) {
          const walkSpeed = 0.48 * pet.direction;
          pet.vx += (walkSpeed - pet.vx) * 0.045;
        } else if (pet.onGround) {
          pet.vx *= 0.82;
        }

        pet.vy += 0.48 * tick;
        pet.vx *= pet.onGround ? 0.986 : 0.994;
        pet.x += pet.vx * tick;
        pet.y += pet.vy * tick;

        if (pet.y < 0) {
          pet.y = 0;
          pet.vy = Math.max(1.8, Math.abs(pet.vy) * 0.35);
          pet.onGround = false;

          if (time - pet.lastTopMessageAt > 2600) {
            say("Bonk. The top of the screen pushed me back down.", 1900);
            pet.lastTopMessageAt = time;
          }
        }

        if (pet.x <= 10) {
          pet.x = 10;
          pet.direction = 1;
          pet.vx = Math.max(Math.abs(pet.vx), 0.55);

          if (time - pet.lastEdgeMessageAt > 2200) {
            say(getLine("edge"), 1900);
            pet.lastEdgeMessageAt = time;
          }
        }

        if (pet.x + width >= window.innerWidth - 10) {
          pet.x = window.innerWidth - width - 10;
          pet.direction = -1;
          pet.vx = -Math.max(Math.abs(pet.vx), 0.55);

          if (time - pet.lastEdgeMessageAt > 2200) {
            say(getLine("edge"), 1900);
            pet.lastEdgeMessageAt = time;
          }
        }

        collideWithPlatforms(previousY);

        if (pet.footerLimitTop !== null && pet.y + height > pet.footerLimitTop) {
          pet.y = pet.footerLimitTop - height;
          pet.vy = 0;
          pet.onGround = true;
        }

        maybeRandomJump(time);
        maybeSpeakFromVisibleText(time);

        if (pet.y < 64 && time - pet.lastTopMessageAt > 5000) {
          say("I can see the whole page from here.", 2300);
          pet.lastTopMessageAt = time;
        }

        if (pet.y > window.innerHeight + 180) {
          pet.x = clamp(window.innerWidth * 0.5, 20, window.innerWidth - width - 20);
          pet.y = -height;
          pet.vy = 2;
          pet.onGround = false;
          say("Soft landing attempt number one.", 2100);
        }
      }

      applyPosition();
      setSpriteForState(chooseState(), delta);

      pet.raf = window.requestAnimationFrame(step);
    };

    const onPointerEnter = () => {
      if (pet.dragging) return;

      pet.pauseUntil = performance.now() + 850;
      pet.lastUserActionAt = performance.now();

      say(getLine("hover"), 2100);
    };

    const onPointerDown = (event) => {
      const width = getWidth();
      const height = getHeight();

      event.preventDefault();
      node.setPointerCapture?.(event.pointerId);

      pet.dragging = true;
      pet.pointerId = event.pointerId;
      pet.dragOffsetX = event.clientX - pet.x;
      pet.dragOffsetY = event.clientY - pet.y;
      pet.pointerLastX = event.clientX;
      pet.pointerLastY = event.clientY;
      pet.pointerLastTime = performance.now();
      pet.pointerVelocityX = 0;
      pet.pointerVelocityY = 0;
      pet.dragDistance = 0;
      pet.wasDragged = false;
      pet.onGround = false;
      pet.vx = 0;
      pet.vy = 0;
      pet.x = clamp(event.clientX - pet.dragOffsetX, 8, window.innerWidth - width - 8);
      pet.y = clamp(event.clientY - pet.dragOffsetY, 8, window.innerHeight - height - 8);

      node.classList.add("site-pet--dragging");

      say("Okay, I trust your hands.", 1900);
      applyPosition();

      pet.lastUserActionAt = performance.now();
    };

    const onPointerMove = (event) => {
      if (!pet.dragging || event.pointerId !== pet.pointerId) return;

      const width = getWidth();
      const height = getHeight();
      const now = performance.now();
      const deltaTime = Math.max(now - pet.pointerLastTime, 12);
      const nextX = clamp(event.clientX - pet.dragOffsetX, 8, window.innerWidth - width - 8);
      const nextY = clamp(event.clientY - pet.dragOffsetY, 8, window.innerHeight - height - 8);

      pet.pointerVelocityX =
        ((event.clientX - pet.pointerLastX) / deltaTime) * 16.67;
      pet.pointerVelocityY =
        ((event.clientY - pet.pointerLastY) / deltaTime) * 16.67;

      pet.dragDistance +=
        Math.abs(event.clientX - pet.pointerLastX) +
        Math.abs(event.clientY - pet.pointerLastY);

      pet.wasDragged = pet.dragDistance > 6;
      pet.pointerLastX = event.clientX;
      pet.pointerLastY = event.clientY;
      pet.pointerLastTime = now;
      pet.direction =
        pet.pointerVelocityX < -0.2
          ? -1
          : pet.pointerVelocityX > 0.2
            ? 1
            : pet.direction;

      pet.x = nextX;
      pet.y = nextY;

      applyPosition();

      pet.lastUserActionAt = performance.now();
    };

    const releaseDrag = (event) => {
      if (!pet.dragging || event.pointerId !== pet.pointerId) return;

      node.releasePointerCapture?.(event.pointerId);
      node.classList.remove("site-pet--dragging");

      pet.dragging = false;
      pet.pointerId = null;
      pet.vx = clamp(pet.pointerVelocityX, -4.5, 4.5);
      pet.vy = clamp(pet.pointerVelocityY, -6, 5.5);
      pet.direction = pet.vx < -0.2 ? -1 : pet.vx > 0.2 ? 1 : pet.direction;

      recordPetEvent("drag");
      say("Thanks. I will take it from here.", 2200);

      pet.interactionCount += 2;
      pet.lastInteractionAt = performance.now();
      pet.lastUserActionAt = performance.now();

      maybeTriggerHappy();
    };

    const onClick = () => {
      if (pet.wasDragged) {
        pet.wasDragged = false;
        return;
      }

      const now = performance.now();

      recordPetEvent("click");

      pet.pauseUntil = now + 250;
      pet.vy = pet.onGround ? -7.4 : pet.vy - 1.6;
      pet.onGround = false;
      pet.lastPlatformElement = null;

      say(getLine("click"), 2100);

      pet.interactionCount += 1;
      pet.lastInteractionAt = performance.now();
      pet.clickCount += 1;
      pet.lastUserActionAt = performance.now();

      maybeTriggerHappy();
      maybeTriggerTextMsg();
    };

    const onKeyDown = (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;

      event.preventDefault();
      onClick();
    };

    const onScroll = () => {
      const currentScrollY =
        window.scrollY || document.documentElement.scrollTop || 0;
      const scrollDelta = currentScrollY - pet.lastScrollY;
      const now = performance.now();

      recordPetEvent("scroll");

      pet.interactionCount += 1;
      pet.lastInteractionAt = performance.now();
      pet.lastScrollY = currentScrollY;
      pet.lastUserActionAt = performance.now();

      maybeTriggerHappy();
      refreshPlatforms();

      if (Math.abs(scrollDelta) < 1 || pet.dragging) return;

      if (now - pet.lastScrollMessageAt > 2800) {
        say(scrollDelta > 0 ? getLine("scroll") : "Back up we go, carefully.", 1700);
        pet.lastScrollMessageAt = now;
      }
    };

    const onResize = () => {
      const width = getWidth();
      const height = getHeight();

      pet.x = clamp(pet.x, 8, window.innerWidth - width - 8);
      pet.y = clamp(pet.y, 8, window.innerHeight - height - 8);

      refreshPlatforms();
      applyPosition();
    };

    node.addEventListener("pointerenter", onPointerEnter);
    node.addEventListener("pointerdown", onPointerDown);
    node.addEventListener("click", onClick);
    node.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", releaseDrag);
    window.addEventListener("pointercancel", releaseDrag);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    placePet();
    say("Hi, I live here now.", 2600);

    pet.raf = window.requestAnimationFrame(step);
    pet.chatterTimer = window.setInterval(() => {
      if (!document.hidden && !pet.dragging && Math.random() > 0.42) {
        say(getLine("random"), 2300);
      }
    }, 8200);

    return () => {
      window.cancelAnimationFrame(pet.raf);
      window.clearInterval(pet.chatterTimer);
      window.clearTimeout(speechTimerRef.current);

      node.removeEventListener("pointerenter", onPointerEnter);
      node.removeEventListener("pointerdown", onPointerDown);
      node.removeEventListener("click", onClick);
      node.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", releaseDrag);
      window.removeEventListener("pointercancel", releaseDrag);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [getLine, say]);

  return (
    <div
      ref={petRef}
      className={`site-pet site-pet--${stateName}`}
      data-site-pet
      data-pet-state={stateName}
      role="button"
      tabIndex={0}
      aria-label="Interactive site pet"
    >
      <div
        className={`site-pet__bubble ${speech ? "is-visible" : ""}`}
        data-pet-bubble
        aria-live="polite"
      >
        {speech}
      </div>

      <img
        ref={spriteRef}
        className="site-pet__sprite"
        src={spritePackRef.current.idle[0]}
        alt=""
        draggable="false"
      />
    </div>
  );
}

export default SitePet;
